/**
 * SkeletonText — a paragraph of placeholder lines.
 *
 * Real copy rarely fills its last line, so the final bar is narrowed by
 * default; without that a multi-line block reads as a solid slab. Lines are
 * sized from a {@link TextSize} token, so a placeholder for `md/semibold` copy
 * occupies the same vertical space the copy will.
 */
import { useMemo, type FC } from "react";
import type { DimensionValue, StyleProp, ViewStyle } from "react-native";
import "../../theme"; // module augmentation for react-native-unistyles types
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View } from "../View";
import type { AppSpacing } from "../../theme";
import type { TextSize } from "../Text";
import { Skeleton } from "./Skeleton";
import type { SkeletonAnimation } from "./Skeleton.types";

export interface SkeletonTextProps {
  /**
   * Number of placeholder lines. Values below one render nothing.
   *
   * @default 3
   */
  readonly lines?: number | undefined;
  /**
   * Text scale the lines are measured against.
   *
   * @default "sm/regular"
   */
  readonly textStyle?: TextSize | undefined;
  /** Width of every line but the last. @default "100%" */
  readonly width?: DimensionValue | undefined;
  /**
   * Width of the last line, mimicking a partially filled line of copy. Ignored
   * when there is only one line.
   *
   * @default "60%"
   */
  readonly lastLineWidth?: DimensionValue | undefined;
  /**
   * Extra vertical space between lines. Defaults to none: each line already
   * occupies a full line box, so an unspaced block is exactly as tall as the
   * copy it stands in for.
   */
  readonly gap?: AppSpacing | undefined;
  /** Overrides the animation inherited from an enclosing `SkeletonGroup`. */
  readonly animation?: SkeletonAnimation | undefined;
  /**
   * Announced by screen readers for the block as a whole. When omitted the
   * block is hidden from assistive technology.
   */
  readonly accessibilityLabel?: string | undefined;
  readonly style?: StyleProp<ViewStyle> | undefined;
  readonly testID?: string | undefined;
}

const DEFAULT_LINES = 3;
const DEFAULT_LAST_LINE_WIDTH: DimensionValue = "60%";

const SkeletonText: FC<SkeletonTextProps> = ({
  lines = DEFAULT_LINES,
  textStyle,
  width = "100%",
  lastLineWidth = DEFAULT_LAST_LINE_WIDTH,
  gap,
  animation,
  accessibilityLabel,
  style,
  testID,
}) => {
  const { theme } = useUnistyles();
  const rowGap = gap !== undefined ? theme.spacing[gap] : 0;

  const lineWidths = useMemo((): readonly DimensionValue[] => {
    const count = Math.max(0, Math.floor(lines));
    return Array.from({ length: count }, (_unused, index) =>
      index === count - 1 && count > 1 ? lastLineWidth : width,
    );
  }, [lines, width, lastLineWidth]);

  if (lineWidths.length === 0) return null;

  return (
    <View
      style={[{ rowGap }, style]}
      {...(accessibilityLabel !== undefined ?
        { accessible: true, accessibilityRole: "progressbar" as const, accessibilityLabel }
      : {})}
      {...(testID !== undefined ? { testID } : {})}
    >
      {lineWidths.map((lineWidth, index) => (
        <Skeleton
          // Lines are positional and interchangeable, so the index is the identity.
          key={index}
          variant="text"
          width={lineWidth}
          {...(textStyle !== undefined ? { textStyle } : {})}
          {...(animation !== undefined ? { animation } : {})}
          style={styles.line}
        />
      ))}
    </View>
  );
};

SkeletonText.displayName = "SkeletonText";

export { SkeletonText };

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create(() => ({
  line: {
    // Widths are percentages of the block, so lines must not stretch.
    alignSelf: "flex-start",
  },
}));
